import { ReactNode } from 'react';
import { Outlet } from 'react-router';

import { AzimutLogoShort } from '@/app/components/AzimutLogoShort';
import { ChevronBackButton } from '@/app/components/ChevronBackButton';
import { ExternalLinkButton } from '@/app/components/ExternalLinkButton';
import { PageNavigationDropdown } from '@/app/components/PageNavigationDropdown';
import ProfileButton from '@/app/components/ProfileButton';
import { NavigationConfig } from '@/app/types/navigation';
import { Toaster } from '@/common/components/ui/sonner';
import { usePagesNavigation } from '@/common/hooks/usePagesNavigation';

interface RootLayoutProps {
  children: ReactNode;
  navigationConfig: NavigationConfig;
  noPadding?: boolean;
}

export const RootLayoutContent = ({
  children,
  navigationConfig,
  noPadding = false,
}: RootLayoutProps) => {
  return (
    <div className="flex flex-col h-screen">
      <Topbar navigationConfig={navigationConfig} />
      <main
        className={`flex-1 w-full h-[calc(100vh-3.5rem)] mt-14 ${noPadding ? 'overflow-hidden' : 'overflow-auto p-4 md:p-6'}`}
      >
        {children}
      </main>
      <Toaster position="top-right" />
    </div>
  );
};

export default function RootLayout() {
  return <Outlet />;
}

/* -------------------------------- Topbar -------------------------------- */

interface TopbarProps {
  navigationConfig: NavigationConfig;
}

const Topbar = ({ navigationConfig }: TopbarProps) => {
  const { backAction, cta } = navigationConfig;
  const { pages, currentPage } = usePagesNavigation();

  return (
    <header className="fixed inset-x-0 top-0 z-50 h-14 border-b flex items-center gap-2 px-4 bg-terre">
      <div className="flex-1 flex items-center justify-between gap-2">
        <div className="flex flex-row items-center">
          <AzimutLogoShort width={38} />
          {backAction.hasBackAction && (
            <ChevronBackButton onBack={backAction.onBack} className="ml-2.5" />
          )}
          <div className="ml-2">
            <PageNavigationDropdown currentPage={currentPage} pages={pages} />
          </div>
          {cta && <ExternalLinkButton href={cta} className="ml-2" />}
        </div>
        <div className="flex items-center gap-2">
          <ProfileButton />
        </div>
      </div>
    </header>
  );
};
